import { Component } from '@theme/component';
import { track } from '@theme/r-analytics';

/* ↗ .cursor/references/code-notes/r-store-locator.js.md#§1 */

/** How long to wait for the app to paint before declaring failure. */
const MOUNT_TIMEOUT_MS = 15_000;

/** Poll interval while waiting for the app's first paint. */
const MOUNT_POLL_MS = 250;

class RStoreLocator extends Component {
  #timers = new Set();
  #injected = false;
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#publishConfig();
    this.#waitForMount();

    // WS23 — the app bundle is vendored, so analytics hooks observe its DOM from here.
    const { signal } = this.#abort;
    this.addEventListener('click', this.#trackClick, { capture: true, passive: true, signal });
    this.addEventListener('keydown', this.#trackKeydown, { capture: true, passive: true, signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    super.disconnectedCallback();
  }

  /** The typed city / postcode; the app owns the input, we only read it. */
  get #query() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('#mapbox-search-input'));
    return input?.value.trim() || undefined;
  }

  /** Store name for an interaction: the details dialog's title, else the card's heading. */
  #storeNameFor(element) {
    const dialog = element.closest('[role="dialog"]');
    const heading = dialog ? dialog.querySelector('#store-modal-title, h2') : element.closest('[role="listitem"]')?.querySelector('h3');
    return heading?.textContent?.trim() || undefined;
  }

  /** @param {KeyboardEvent} event */
  #trackKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    if (event.target.id !== 'mapbox-search-input') return;
    // Enter on a highlighted suggestion is the app's own select; it reports the picked name.
    const active = event.target.getAttribute('aria-activedescendant');
    const query = active ? document.getElementById(active)?.textContent?.trim() : this.#query;
    if (query) track('store_locator', { action: 'search', query });
  };

  /** @param {MouseEvent} event */
  #trackClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const option = target.closest('[role="option"]');
    if (option) {
      track('store_locator', { action: 'search', query: option.querySelector('div')?.textContent?.trim() || this.#query });
      return;
    }

    const button = target.closest('button');
    if (button?.getAttribute('aria-label') === 'Search') {
      const query = this.#query;
      if (query) track('store_locator', { action: 'search', query });
      return;
    }
    if (button?.getAttribute('aria-label') === 'Use my location') {
      track('store_locator', { action: 'search', query: 'my_location' });
      return;
    }

    const anchor = target.closest('a[href]');
    if (anchor) {
      const href = anchor.getAttribute('href') ?? '';
      const action = href.startsWith('tel:') ? 'call' : href.startsWith('mailto:') ? 'email' : href.includes('google.com/maps/dir') ? 'directions' : null;
      if (action) track('store_locator', { action, store_name: this.#storeNameFor(anchor) });
      return;
    }

    // A result card, its details button, or a map marker — all select a store.
    const card = target.closest('[role="listitem"]');
    const marker = target.closest('[role="button"][aria-label]');
    if (card || (marker && !card && marker.getAttribute('aria-label')?.includes(' - '))) {
      const name = card ? this.#storeNameFor(card) : marker?.getAttribute('aria-label')?.split(' - ')[0];
      track('store_locator', { action: 'select_store', store_name: name, query: this.#query });
    }
  };

  /* ↗ .cursor/references/code-notes/r-store-locator.js.md#§2 */
  #publishConfig() {
    const config = {
      apiUrl: this.dataset.apiUrl || '',
      mapboxToken: this.dataset.mapboxToken || '',
      mapboxVersion: this.dataset.mapboxVersion || '',
    };

    window.RockyStoreLocator = Object.assign({}, window.RockyStoreLocator, config);
  }

  /** The element the app bundle renders into. */
  get #root() {
    return this.querySelector('#store-locator-root');
  }

  /* ↗ .cursor/references/code-notes/r-store-locator.js.md#§3 */
  #isMounted() {
    const root = this.#root;
    if (!root) return false;

    for (const child of root.children) {
      if (!child.classList.contains('r-store-locator__loading')) return true;
    }
    return false;
  }

  #after(ms, fn) {
    const timer = setTimeout(() => {
      this.#timers.delete(timer);
      fn();
    }, ms);
    this.#timers.add(timer);
  }

  /* ↗ .cursor/references/code-notes/r-store-locator.js.md#§4 */
  #waitForMount() {
    const deadline = Date.now() + MOUNT_TIMEOUT_MS;

    const poll = () => {
      if (!this.isConnected || this.#isMounted()) return;

      if (!this.#injected && document.readyState === 'complete') {
        this.#injectApp();
      }

      if (Date.now() >= deadline) {
        this.#showError();
        return;
      }

      this.#after(MOUNT_POLL_MS, poll);
    };

    this.#after(MOUNT_POLL_MS, poll);
  }

  #injectApp() {
    const src = this.dataset.appSrc;
    if (!src) return;

    this.#injected = true;

    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.addEventListener('error', () => this.#showError(), { once: true });
    document.head.appendChild(script);
  }

  #showError() {
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();

    const root = this.#root;
    if (root && !this.#isMounted()) root.hidden = true;

    const error = this.refs.error;
    if (error) error.hidden = false;
  }
}

if (!customElements.get('r-store-locator')) {
  customElements.define('r-store-locator', RStoreLocator);
}
